// All useState declarations extracted from OfferPageBuilderSimple.js lines 283-657
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

export const useOfferBuilderState = ({
  templatesOnly,
  isBrandsPage,
  templateSelectionOnly,
  sharedElements,
  setSharedElements,
  sharedPages,
  setSharedPages,
  sharedSelectedElement,
  setSharedSelectedElement,
  sharedSelectedElementContext,
  setSharedSelectedElementContext,
  sharedSelectedCategory,
  setSharedSelectedCategory,
  sharedSelectedSubtype,
  setSharedSelectedSubtype,
  sharedSelectedBrand,
  setSharedSelectedBrand,
  sharedSelectedOffer,
  setSharedSelectedOffer,
  sharedSelectedTemplateId,
  setSharedSelectedTemplateId,
}) => {
  // Use shared state if provided, otherwise use local state
  const [localElements, setLocalElements] = useState([]);
  const [localSelectedElement, setLocalSelectedElement] = useState(null);
  const [localSelectedElementContext, setLocalSelectedElementContext] = useState(null);

  // Pagination state for offer elements
  // Use shared pages state if provided, otherwise override with local state
  const [localPages, setLocalPages] = useState([{ id: 1, elements: [] }]);
  const pages = sharedPages || localPages;
  const setPages = setSharedPages || setLocalPages;
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [nextPageId, setNextPageId] = useState(2);
  const [paginationEnabled, setPaginationEnabled] = useState(true); // Always enabled for manual page control
  const [isLoadingExistingContent, setIsLoadingExistingContent] = useState(false); // Flag to prevent auto-pagination
  const [insertAfterElementId, setInsertAfterElementId] = useState(null); // Store element ID to insert after when using add button
  const [localRatingText, setLocalRatingText] = useState(''); // Local state for rating text field to avoid focus loss

  // Determine which state to use
  const getCurrentPage = useCallback(() => {
    return pages[currentPageIndex] || pages[0];
  }, [pages, currentPageIndex]);

  const currentPageElements = getCurrentPage()?.elements || [];
  // Smart elements logic: use pagination when enabled and pages exist
  const elements = (paginationEnabled && pages.length > 0)
    ? currentPageElements
    : (sharedPages
      ? sharedPages.reduce((acc, p) => [...acc, ...(p.elements || [])], [])
      : (sharedElements !== null ? sharedElements : localElements));
  const setElements = setSharedElements !== null ? setSharedElements : setLocalElements;
  const selectedElement = sharedSelectedElement !== null ? sharedSelectedElement : localSelectedElement;
  const setSelectedElement = setSharedSelectedElement !== null ? setSharedSelectedElement : setLocalSelectedElement;
  const selectedElementContext = sharedSelectedElementContext !== null ? sharedSelectedElementContext : localSelectedElementContext;
  const setSelectedElementContext = setSharedSelectedElementContext !== null ? setSharedSelectedElementContext : setLocalSelectedElementContext;

  // Determine if we're in editing mode (actively creating/editing content)
  const isEditingMode = useMemo(() => {
    const hasContent = localElements.length > 0 || (sharedElements !== null && Array.isArray(sharedElements) && sharedElements.length > 0);
    const hasSelectedElement = selectedElement !== null;

    const result = (hasContent || hasSelectedElement) && !templatesOnly && !isBrandsPage;

    console.log('🔍 isEditingMode calculation:', {
      hasContent,
      hasSelectedElement,
      templatesOnly,
      isBrandsPage,
      result,
      localElementsLength: localElements.length,
      sharedElementsLength: sharedElements?.length || 0,
      selectedElementId: selectedElement?.id
    });

    return result;
  }, [localElements.length, templatesOnly, isBrandsPage, sharedElements, selectedElement]);

  const [selectedSubElement, setSelectedSubElement] = useState(null); // For sub-element selection within composite elements
  const [drawerOpen, setDrawerOpen] = useState(!templatesOnly); // Open in Setups (template creation), closed in Brands (templatesOnly)
  const [previewPanelOpen, setPreviewPanelOpen] = useState(true);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [pageSettingsOpen, setPageSettingsOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showLayoutSelector, setShowLayoutSelector] = useState(false);
  const [targetZone, setTargetZone] = useState(null); // For nested layouts
  const [draggedElement, setDraggedElement] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null);
  const [editingElement, setEditingElement] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');

  // Undo system state
  const [deletedElementsHistory, setDeletedElementsHistory] = useState([]);
  const [showUndoNotification, setShowUndoNotification] = useState(false);

  // Content tab editing state
  const [contentTabTempValue, setContentTabTempValue] = useState('');
  const [buttonContentTabTempValue, setButtonContentTabTempValue] = useState('');
  const [buttonUrlTabTempValue, setButtonUrlTabTempValue] = useState('');
  const [listItemTextValues, setListItemTextValues] = useState({});
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [preBuiltTemplates, setPreBuiltTemplates] = useState([]);
  const [buttonImageCalloutTemplates, setButtonImageCalloutTemplates] = useState([]);
  const [buttonNoticeTemplates, setButtonNoticeTemplates] = useState([]);
  const [buttonTextTemplates, setButtonTextTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [templateSubtype, setTemplateSubtype] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Suppress ResizeObserver errors
  useEffect(() => {
    const errorHandler = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.stopImmediatePropagation();
        console.log('ResizeObserver error suppressed - this is normal and harmless');
        return;
      }
    };

    const unhandledRejectionHandler = (e) => {
      if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
        e.preventDefault();
        console.log('ResizeObserver promise rejection suppressed - this is normal and harmless');
      }
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', unhandledRejectionHandler);

    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
    };
  }, []);

  const [showTemplatesList, setShowTemplatesList] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState(null); // Track currently loaded template
  const [pageSettings, setPageSettings] = useState({
    title: 'My Offer Page',
    backgroundColor: '#ffffff',
    maxWidth: '800px',
    fontFamily: 'Arial, sans-serif'
  });

  // Mobile-specific state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Right panel state
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('mobile'); // mobile, tablet, desktop
  const [forceRender, setForceRender] = useState(0);
  const textEditorRef = useRef(null);

  // Local state for category and subtype
  const [localSelectedCustomCategory, setLocalSelectedCustomCategory] = useState('Headline/Subheadline');
  const [localSelectedCustomSubtype, setLocalSelectedCustomSubtype] = useState(null);

  // Determine which state to use - shared if available, otherwise local
  const selectedCustomCategory = setSharedSelectedCategory ? sharedSelectedCategory : localSelectedCustomCategory;
  const setSelectedCustomCategory = setSharedSelectedCategory || setLocalSelectedCustomCategory;
  const selectedCustomSubtype = setSharedSelectedSubtype ? sharedSelectedSubtype : localSelectedCustomSubtype;
  const setSelectedCustomSubtype = setSharedSelectedSubtype || setLocalSelectedCustomSubtype;

  // Debug effect to monitor category changes
  useEffect(() => {
    console.log('🏷️ selectedCustomCategory changed to:', selectedCustomCategory);
  }, [selectedCustomCategory]);

  // Debug effect to monitor selectedElement changes
  useEffect(() => {
    console.log('🎯 selectedElement changed:', selectedElement?.id, selectedElement?.styles?.fontFamily);
    if (selectedElement) {
      console.log('🎨 Full selectedElement styles:', selectedElement.styles);
      console.log('🎨 Full selectedElement textStyles:', selectedElement.textStyles);
    }
  }, [selectedElement]);

  // Add state for filtered templates by type and subtype
  const [filteredTemplates, setFilteredTemplates] = useState([]);

  // Add state for selected variant key
  const [selectedVariantKey, setSelectedVariantKey] = useState(null);

  // Add state for selected template (use shared if available)
  const [localSelectedTemplateId, setLocalSelectedTemplateId] = useState(null);
  const selectedTemplateId = setSharedSelectedTemplateId ? sharedSelectedTemplateId : localSelectedTemplateId;
  const setSelectedTemplateId = setSharedSelectedTemplateId || setLocalSelectedTemplateId;

  // Add state for icon selector in custom lists
  const [iconSelectorOpen, setIconSelectorOpen] = useState(null);

  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadingElementId, setUploadingElementId] = useState(null);

  // Link generation state
  const [generatingLink, setGeneratingLink] = useState(false);

  // Saved offer elements state
  const [savedOfferElements, setSavedOfferElements] = useState([]);
  const [filteredSavedElements, setFilteredSavedElements] = useState([]);
  const [currentElementName, setCurrentElementName] = useState('');
  const [showSaveElementDialog, setShowSaveElementDialog] = useState(false);
  const [showBrandOfferAlert, setShowBrandOfferAlert] = useState(false);
  const [showLoadTemplateAlert, setShowLoadTemplateAlert] = useState(false);
  const [loadTemplateError, setLoadTemplateError] = useState('');
  const [showStatusAlert, setShowStatusAlert] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTitle, setStatusTitle] = useState('');
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [promptDialogTitle, setPromptDialogTitle] = useState('');
  const [promptDialogMessage, setPromptDialogMessage] = useState('');
  const [promptDialogValue, setPromptDialogValue] = useState('');
  const [promptAction, setPromptAction] = useState(null);
  const [pendingTemplateData, setPendingTemplateData] = useState(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameElementId, setRenameElementId] = useState(null);
  const [renameElementValue, setRenameElementValue] = useState('');
  const [isUpdatingElement, setIsUpdatingElement] = useState(false);
  const [selectedSavedElementId, setSelectedSavedElementId] = useState(null);
  const [saveType, setSaveType] = useState('element'); // 'element' or 'template'

  // Brand and Offer selection state
  const [localSelectedBrand, setLocalSelectedBrand] = useState('');
  const [localSelectedOffer, setLocalSelectedOffer] = useState('');

  // Use shared state if provided, otherwise use local state
  const selectedBrand = sharedSelectedBrand !== null ? sharedSelectedBrand : localSelectedBrand;
  const setSelectedBrand = setSharedSelectedBrand !== null ? setSharedSelectedBrand : setLocalSelectedBrand;
  const selectedOffer = sharedSelectedOffer !== null ? sharedSelectedOffer : localSelectedOffer;
  const setSelectedOffer = setSharedSelectedOffer !== null ? setSharedSelectedOffer : setLocalSelectedOffer;
  const [brands, setBrands] = useState([]);
  const [offers, setOffers] = useState([]);

  // Zone height management state
  const [zoneHeightModes, setZoneHeightModes] = useState({}); // zoneId -> 'third' | 'half' | 'full'
  const [heightOverflowAlerts, setHeightOverflowAlerts] = useState({}); // zoneId -> { hasOverflow: boolean, overflowHeight: number }

  // Loading state
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingOffers, setLoadingOffers] = useState(false);

  // Image resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartData, setResizeStartData] = useState(null);
  const isResizingRef = useRef(false);
  const resizeStartDataRef = useRef(null);
  const resizeDirectionRef = useRef(null);

  // Sync localRatingText when selected element changes
  useEffect(() => {
    if (selectedElement) {
      setLocalRatingText(
        selectedElement.ratingText !== undefined
          ? selectedElement.ratingText
          : `${selectedElement.rating || 5}/${selectedElement.maxStars || 5}`
      );
    }
  }, [selectedElement?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close icon selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (iconSelectorOpen && !event.target.closest('.icon-selector-popup')) {
        setIconSelectorOpen(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [iconSelectorOpen]);

  // Debug effect to track selectedElement changes
  useEffect(() => {
    console.log('🎯 selectedElement changed:', selectedElement);
    console.log('🎯 selectedElementContext:', selectedElementContext);
    console.log('🎯 rightPanelOpen:', rightPanelOpen);
    console.log('🎯 tabValue:', tabValue);
  }, [selectedElement, selectedElementContext, rightPanelOpen, tabValue]);

  return {
    // Elements state
    localElements,
    setLocalElements,
    localSelectedElement,
    setLocalSelectedElement,
    localSelectedElementContext,
    setLocalSelectedElementContext,
    localPages,
    setLocalPages,
    pages,
    setPages,
    currentPageIndex,
    setCurrentPageIndex,
    nextPageId,
    setNextPageId,
    paginationEnabled,
    setPaginationEnabled,
    isLoadingExistingContent,
    setIsLoadingExistingContent,
    insertAfterElementId,
    setInsertAfterElementId,
    localRatingText,
    setLocalRatingText,
    getCurrentPage,
    currentPageElements,
    elements,
    setElements,
    selectedElement,
    setSelectedElement,
    selectedElementContext,
    setSelectedElementContext,
    isEditingMode,
    selectedSubElement,
    setSelectedSubElement,
    drawerOpen,
    setDrawerOpen,
    previewPanelOpen,
    setPreviewPanelOpen,
    settingsOpen,
    setSettingsOpen,
    settingsAnchorEl,
    setSettingsAnchorEl,
    pageSettingsOpen,
    setPageSettingsOpen,
    tabValue,
    setTabValue,
    popoverPosition,
    setPopoverPosition,
    isDragging,
    setIsDragging,
    dragOffset,
    setDragOffset,
    showLayoutSelector,
    setShowLayoutSelector,
    targetZone,
    setTargetZone,
    draggedElement,
    setDraggedElement,
    dragOverZone,
    setDragOverZone,
    editingElement,
    setEditingElement,
    editingField,
    setEditingField,
    tempValue,
    setTempValue,
    deletedElementsHistory,
    setDeletedElementsHistory,
    showUndoNotification,
    setShowUndoNotification,
    contentTabTempValue,
    setContentTabTempValue,
    buttonContentTabTempValue,
    setButtonContentTabTempValue,
    buttonUrlTabTempValue,
    setButtonUrlTabTempValue,
    listItemTextValues,
    setListItemTextValues,
    savedTemplates,
    setSavedTemplates,
    preBuiltTemplates,
    setPreBuiltTemplates,
    buttonImageCalloutTemplates,
    setButtonImageCalloutTemplates,
    buttonNoticeTemplates,
    setButtonNoticeTemplates,
    buttonTextTemplates,
    setButtonTextTemplates,
    templateName,
    setTemplateName,
    templateType,
    setTemplateType,
    templateSubtype,
    setTemplateSubtype,
    showSaveDialog,
    setShowSaveDialog,
    showTemplatesList,
    setShowTemplatesList,
    currentTemplateId,
    setCurrentTemplateId,
    pageSettings,
    setPageSettings,
    mobileDrawerOpen,
    setMobileDrawerOpen,
    rightPanelOpen,
    setRightPanelOpen,
    previewDevice,
    setPreviewDevice,
    forceRender,
    setForceRender,
    textEditorRef,
    selectedCustomCategory,
    setSelectedCustomCategory,
    selectedCustomSubtype,
    setSelectedCustomSubtype,
    filteredTemplates,
    setFilteredTemplates,
    selectedVariantKey,
    setSelectedVariantKey,
    selectedTemplateId,
    setSelectedTemplateId,
    iconSelectorOpen,
    setIconSelectorOpen,
    imageUploading,
    setImageUploading,
    uploadingElementId,
    setUploadingElementId,
    generatingLink,
    setGeneratingLink,
    savedOfferElements,
    setSavedOfferElements,
    filteredSavedElements,
    setFilteredSavedElements,
    currentElementName,
    setCurrentElementName,
    showSaveElementDialog,
    setShowSaveElementDialog,
    showBrandOfferAlert,
    setShowBrandOfferAlert,
    showLoadTemplateAlert,
    setShowLoadTemplateAlert,
    loadTemplateError,
    setLoadTemplateError,
    showStatusAlert,
    setShowStatusAlert,
    statusMessage,
    setStatusMessage,
    statusTitle,
    setStatusTitle,
    showReplaceConfirm,
    setShowReplaceConfirm,
    showConfirmDialog,
    setShowConfirmDialog,
    confirmDialogTitle,
    setConfirmDialogTitle,
    confirmDialogMessage,
    setConfirmDialogMessage,
    confirmAction,
    setConfirmAction,
    showPromptDialog,
    setShowPromptDialog,
    promptDialogTitle,
    setPromptDialogTitle,
    promptDialogMessage,
    setPromptDialogMessage,
    promptDialogValue,
    setPromptDialogValue,
    promptAction,
    setPromptAction,
    pendingTemplateData,
    setPendingTemplateData,
    showRenameDialog,
    setShowRenameDialog,
    renameElementId,
    setRenameElementId,
    renameElementValue,
    setRenameElementValue,
    isUpdatingElement,
    setIsUpdatingElement,
    selectedSavedElementId,
    setSelectedSavedElementId,
    saveType,
    setSaveType,
    selectedBrand,
    setSelectedBrand,
    selectedOffer,
    setSelectedOffer,
    brands,
    setBrands,
    offers,
    setOffers,
    zoneHeightModes,
    setZoneHeightModes,
    heightOverflowAlerts,
    setHeightOverflowAlerts,
    loadingBrands,
    setLoadingBrands,
    loadingOffers,
    setLoadingOffers,
    isResizing,
    setIsResizing,
    resizeDirection,
    setResizeDirection,
    resizeStartData,
    setResizeStartData,
    isResizingRef,
    resizeStartDataRef,
    resizeDirectionRef,
  };
};
